/**
 * Shared helpers for converting raw markdown snippets to clean, human
 * readable text and stable slug ids. Keep this logic centralized so that
 * every place that pulls text out of a markdown string (table of contents,
 * FAQ extraction, heading anchor ids, og previews, search indexing, etc.)
 * stays in sync and does not leak raw syntax to the UI.
 *
 * Intentionally framework agnostic: no React, no DOM. Pure functions so the
 * same helpers can be used at build time, in server code, and in the browser.
 */

import { repairEmphasis } from "@/lib/contentStyleRules";

export type MaybeReactChild =
  | string
  | number
  | null
  | undefined
  | boolean
  | Iterable<MaybeReactChild>
  | { props?: { children?: MaybeReactChild } };

/**
 * Flatten arbitrary React children (or a plain string) into a single plain
 * string, the same way a browser would render .textContent. Used by the
 * custom markdown heading renderer so that headings containing inline
 * elements (links, emphasis, code) produce sensible ids.
 */
export function childrenToPlainText(node: MaybeReactChild): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (
    typeof (node as any)[Symbol.iterator] === "function" &&
    typeof node !== "string"
  ) {
    let out = "";
    for (const child of node as Iterable<MaybeReactChild>) {
      out += childrenToPlainText(child);
    }
    return out;
  }
  const maybeEl = node as { props?: { children?: MaybeReactChild } };
  if (maybeEl && maybeEl.props && "children" in maybeEl.props) {
    return childrenToPlainText(maybeEl.props.children);
  }
  return "";
}

/**
 * Strip the most common inline markdown syntax from a snippet so the
 * underlying human readable text is left behind. This is deliberately
 * narrow: it does not try to be a full markdown parser, only to keep
 * raw syntax like [text](url), **bold**, _italic_, `code`, and images
 * out of user facing labels such as TOC entries or FAQ questions.
 */
export function stripInlineMarkdown(input: string): string {
  if (!input) return "";
  let out = input;

  // Images: ![alt](url)  -> alt
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");

  // Links: [text](url)   -> text
  out = out.replace(/\[([^\]]+)\]\(([^)]*)\)/g, "$1");

  // Reference style links: [text][ref] -> text
  out = out.replace(/\[([^\]]+)\]\[[^\]]*\]/g, "$1");

  // Bold + italic wrappers (order matters: longest first)
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, "$1");
  out = out.replace(/\*\*([^*]+)\*\*/g, "$1");
  out = out.replace(/\*([^*]+)\*/g, "$1");
  out = out.replace(/___([^_]+)___/g, "$1");
  out = out.replace(/__([^_]+)__/g, "$1");
  out = out.replace(/_([^_]+)_/g, "$1");

  // Inline code backticks
  out = out.replace(/`([^`]+)`/g, "$1");

  // Stray remaining brackets from broken syntax
  out = out.replace(/[\[\]]/g, "");

  return out.replace(/\s+/g, " ").trim();
}

/**
 * Convert any string into the kebab slug form used across the app for
 * anchor ids, cache keys, and route params. Keeping this in one place
 * means the TOC link and the heading id it targets always agree.
 */
export function slugify(input: string): string {
  return (input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Convenience: take an untrusted markdown snippet and return both its
 * display text and its slug id in one call. Handy for TOC builders.
 */
export function toDisplayAndSlug(markdownSnippet: string): {
  text: string;
  id: string;
} {
  const text = stripInlineMarkdown(markdownSnippet);
  return { text, id: slugify(text) };
}

/**
 * Strip the first heading line from markdown content when the rendering
 * surface already shows the title as a dedicated <h1> element (e.g. lesson
 * pages, where the JSX renders currentStep.title and the content redundantly
 * repeats it as a ## heading). Handles #, ##, and ### prefixes.
 */
export function stripLeadingHeading(input: string): string {
  if (!input) return "";
  return input.replace(/^[ \t]*#{1,3}[ \t]+[^\n]*\n?/, "").trimStart();
}

/**
 * Normalize long-form markdown before it hits ReactMarkdown.
 *
 * A single funnel used by every surface that renders user authored body
 * content (blog, guides, lessons). Keeping the sanitizer here means any
 * rendering surface gets the same corrections for free.
 *
 * Concrete corrections:
 *   0. WP "read more" intro separator -- WP sometimes emits a short teaser
 *      paragraph before a <!-- more --> marker that migrates as plain text
 *      followed by a lone --- thematic break within the first 1000 characters
 *      and with no ## heading before it. Strip everything up to and including
 *      that --- so the real article starts cleanly at its first heading or
 *      paragraph.
 *
 *   1. Broken empty link cards -- imported content sometimes contains a
 *      link whose visible text is just whitespace wrapped in tabs and
 *      newlines. CommonMark treats the leading tabs as an indented code
 *      block, so the broken markup renders as literal <code> text. We
 *      collapse the whole pattern to a clean single-line link.
 *
 *   1c. Accidentally indented paragraphs -- generated content occasionally
 *      leaves a normal paragraph indented by 4+ spaces or a tab (e.g.
 *      "    [Contact Us](/contact-us) for..."). CommonMark treats 4+
 *      leading spaces on a line that opens a new block as an indented code
 *      block, so the whole paragraph -- including any markdown link inside
 *      it -- renders as literal preformatted text instead of prose. We
 *      dedent any such line, skipping fenced code blocks and genuine list
 *      continuations.
 *
 *   2. JSX-style CTA tags -- older content may contain raw JSX component
 *      tags such as <HomebuyingSupportCTA /> instead of the canonical
 *      ::HomebuyingSupportCTA:: token. We normalise them to the token form.
 *
 *   3. Inline CTA tokens -- ::FooCTA:: markers must sit on their own line
 *      with blank lines around them so the chunk splitter never cuts a
 *      paragraph in half.
 *
 *   4. Standalone bold lines used as heading substitutes -- imported content
 *      often uses **Bold Text** on its own line instead of proper ## / ###
 *      markdown headings. We rewrite as ### headings.
 *
 *   5. Emoji bullet lines -- content uses emoji like ✅ and 🟢 at the start
 *      of lines as visual bullets. ReactMarkdown renders these as inline text.
 *      We convert them to proper markdown list items (- ✅ text) so they
 *      produce <ul><li> elements with correct spacing and indentation.
 *
 *   6. Missing blank line before list blocks -- imported content often omits
 *      the required blank line before a - or 1. list that follows a paragraph.
 *      ReactMarkdown merges list items into the preceding paragraph as plain
 *      text. We insert the missing blank line before the first item.
 *
 *   7. Collapse runs of 4+ blank lines left behind by the rewrites above.
 */
export function sanitizeBlogMarkdown(input: string): string {
  if (!input) return "";
  let out = repairEmphasis(input);

  // 0. Strip WP "read more" intro separator.
  //    WP teaser paragraphs migrate as plain text followed by a lone ---
  //    thematic break early in the document. When --- appears within the
  //    first 1000 chars with no ## heading before it, everything up to and
  //    including that --- is a WP orphan intro -- discard it.
  const introHrMatch = out.match(
    /^([\s\S]{0,999}?)\n[ \t]*---[ \t]*\n([\s\S]*)$/,
  );
  if (introHrMatch && !/^#{2,}/m.test(introHrMatch[1])) {
    out = introHrMatch[2].trimStart();
  }

  // 1. Strip WP related-post blocks: empty link card + orphan excerpt.
  //    WP injects related-post widgets that export as an empty-text link card
  //    followed by whitespace lines and a truncated excerpt ending with ….
  //    Strip both so neither the junk link nor the orphan text is rendered.
  out = out.replace(
    /(^|\n)[ \t]*\[[\s]*\]\([^)\s]+\)(?:[ \t]*\n)+(?:[^\n\u2026]*\n)*[^\n\u2026]*\u2026/g,
    (_match, lead: string) => lead,
  );
  // 1b. Strip any remaining bare empty-text link cards (no orphan / no … ending).
  out = out.replace(
    /(^|\n)[ \t]*\[[\s]*\]\([^)\s]+\)[ \t]*(?=\n|$)/g,
    (_match, lead: string) => lead,
  );

  // 1c. Dedent accidentally-indented paragraphs (see doc comment above) so
  //     they render as prose instead of a literal code block. Fence-aware:
  //     skips content inside ``` blocks, and leaves list-item continuations
  //     (a line starting with -, *, +, or "1." after its indent) alone,
  //     since those need to keep their indentation for correct nesting.
  out = (() => {
    const lines = out.split("\n");
    let inFence = false;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^[ \t]*```/.test(line)) {
        inFence = !inFence;
        continue;
      }
      if (inFence) continue;
      const startsNewBlock = i === 0 || lines[i - 1].trim() === "";
      const isIndented = /^(?: {4,}|\t)/.test(line);
      const isListContinuation = /^[ \t]*(?:[-*+]|\d+\.)\s/.test(line);
      if (startsNewBlock && isIndented && !isListContinuation) {
        lines[i] = line.replace(/^[ \t]+/, "");
      }
    }
    return lines.join("\n");
  })();

  // 2. Convert JSX-style CTA tags to canonical ::Token:: form and strip
  //    any leftover closing tags.
  out = out.replace(/<([A-Za-z]+CTA)\s*\/?>/g, "::$1::");
  out = out.replace(/<\/[A-Za-z]+CTA>/g, "");

  // 3. Ensure any inline CTA token sits on its own line with blank lines
  //    around it so the chunk splitter never cuts a paragraph in half.
  out = out.replace(/([^\n])(::[A-Za-z]+CTA::)/g, "$1\n\n$2");
  out = out.replace(/(::[A-Za-z]+CTA::)([^\n])/g, "$1\n\n$2");

  // 3b. If a CTA token lands mid-list (list items both before and after it),
  //     push it past the trailing list items so ReactMarkdown never splits
  //     one logical list into two disconnected lists.
  out = out.replace(
    /\n\n(::[A-Za-z]+CTA::)\n\n((?:- [^\n]+\n(?:\n(?=- ))?)+)/g,
    (_match, cta, listItems) => `\n\n${listItems.trimEnd()}\n\n${cta}\n\n`,
  );

  // 4. Promote standalone bold lines used as heading substitutes to ### headings.
  out = out.replace(
    /^[ \t]*\*\*([^*\n]{2,100})\*\*[ \t]*$/gm,
    (_match, text) => `\n\n### ${text.trim()}\n\n`,
  );

  // 5. Split inline bullet-separated lists into proper markdown list items.
  //    Handles two patterns:
  //    a) Inline: "• item1 • item2 • item3" (all on one line) → separate "- item" lines
  //    b) Line-start: "• item" (lone bullet) → "- item"
  //    Both patterns come from WP/CMS imports and static guide content.
  out = out.replace(/^[ \t]*•.+$/gm, (line) =>
    line
      .split(/[ \t]*•[ \t]*/)
      .filter((s) => s.trim().length > 0)
      .map((item) => `- ${item.trim()}`)
      .join("\n"),
  );

  // 5b. Convert en-dash (–) and em-dash (—) at line start used as bullet markers.
  //     CommonMark does not treat these as list markers so they render as
  //     plain paragraph text. Rewrite to standard - so they become <li> elements.
  out = out.replace(/^[ \t]*[–—][ \t]+/gm, "- ");

  // 6. Convert emoji-prefixed lines to proper markdown list items.
  //    Lines that start with ✅, 🟢, 🔵, ☑, or ✓ followed by whitespace
  //    are intended as visual checklist/bullet items. Without a leading "- "
  //    ReactMarkdown renders them as plain paragraph text with no list
  //    structure. We prepend "- " so they become <li> elements.
  out = out.replace(/^[ \t]*(✅|❌|🟢|🔴|🟡|🔵|⚠️|☑|✓)(?=[ \t])/gm, "- $1");

  // 7. Ensure a blank line before list blocks that directly follow paragraph text.
  //    CommonMark requires a blank line between a paragraph and a following list.
  //    When it is missing, ReactMarkdown absorbs the list items into the paragraph
  //    as plain text. We detect the transition from a non-list line to a list item
  //    line and insert the required blank line.
  //    The negative lookahead prevents inserting blank lines between consecutive
  //    list items, which would break the list into separate single-item lists.
  out = out.replace(
    /^(?![ \t]*(?:-|\*|\+|\d+\.)\s)(.*\S.*)\n(?=[ \t]*(?:-|\*|\+|\d+\.)\s)/gm,
    "$1\n\n",
  );

  // 8. Collapse runs of 4+ blank lines left behind by the rewrites above.
  out = out.replace(/\n{4,}/g, "\n\n\n");

  return out;
}

/**
 * Best effort human label for a URL when the original link text was empty.
 * Internal /blog/slug-like-this becomes "Slug Like This". External or
 * opaque links just get a generic "Read more" so we never surface a raw
 * domain or query string as anchor text.
 */
function deriveLinkLabel(href: string): string {
  try {
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//")) {
      return "Read more";
    }
    const clean = href.replace(/[?#].*$/, "").replace(/\/$/, "");
    const lastSeg = clean.split("/").filter(Boolean).pop() || "";
    if (!lastSeg) return "Read more";
    return (
      lastSeg
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase()) || "Read more"
    );
  } catch {
    return "Read more";
  }
}
